import { EarningsPeriod, MovePeriod } from '@api/api/earnings';
import { RangeHorizon } from '@helpers/utils/dates/dates';
import { handlePriceData } from '@terminal/widgets/earnings/utils/tableUtils';
import { PredictionSummaryTooltipParams } from '@terminal/widgets/scenario/components/prediction-summary-table/PredictionSummaryTable';

const HORIZON_TO_MOVE_PERIOD: Record<string, MovePeriod> = {
  [RangeHorizon.OneDay]: 'd1',
  [RangeHorizon.TwoDays]: 'd2',
  [RangeHorizon.OneWeek]: 'w1',
  [RangeHorizon.TwoWeeks]: 'w2',
  [RangeHorizon.OneMonth]: 'm1',
};

export const getPeriodLabel = (earning: EarningsPeriod) =>
  `${earning.fiscal_period} ${earning.fiscal_year}`;

interface GetQuarterTooltipTextParams extends PredictionSummaryTooltipParams {
  selectedEarnings: EarningsPeriod[];
}

export const getQuarterTooltipText = ({
  selectedEarnings,
  rowId,
  horizon,
  value,
}: GetQuarterTooltipTextParams) => {
  const movePeriod = HORIZON_TO_MOVE_PERIOD[horizon];
  if (!movePeriod) {
    return undefined;
  }

  const earningsWithChange = selectedEarnings
    .map(earning => ({
      earning,
      change: handlePriceData(earning.price, movePeriod),
    }))
    .filter(
      (item): item is { earning: EarningsPeriod; change: number } =>
        item.change !== undefined
    );

  if (!earningsWithChange.length) {
    return undefined;
  }

  if (rowId === 'max') {
    return getPeriodLabel(
      earningsWithChange.reduce((best, current) =>
        current.change > best.change ? current : best
      ).earning
    );
  }

  if (rowId === 'min') {
    return getPeriodLabel(
      earningsWithChange.reduce((best, current) =>
        current.change < best.change ? current : best
      ).earning
    );
  }

  if (value === undefined) {
    return undefined;
  }

  // price.change is percentage points; prediction values are decimals
  const targetChange = value * 100;
  return getPeriodLabel(
    earningsWithChange.reduce((best, current) =>
      Math.abs(current.change - targetChange) <
      Math.abs(best.change - targetChange)
        ? current
        : best
    ).earning
  );
};
